import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiEdit, FiTrash2, FiUserPlus, FiUsers, FiX, FiCheck, FiAlertCircle } from 'react-icons/fi';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Form state for add/edit user
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'customer',
    status: 'active',
    phone: '',
    address: '',
    experience: ''
  });

  // Form validation state
  const [validation, setValidation] = useState({
    name: true,
    email: true,
    password: true
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await API.get('/api/user', {
        params: {
          search: searchTerm || undefined,
          role: filterRole !== 'all' ? filterRole : undefined,
          page: currentPage,
          limit: 10
        },
      });

      // Handle pagination data
      if (response.data?.users && Array.isArray(response.data.users)) {
        // Response sudah terformat dengan pagination
        setUsers(response.data.users.map(user => ({
          ...user,
          createdAt: user.created_at ? new Date(user.created_at).toLocaleDateString('id-ID') : '-'
        })));

        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages);
        } else {
          setTotalPages(Math.max(1, Math.ceil(response.data.users.length / 10)));
        }
      } else if (Array.isArray(response.data)) {
        // Response berupa array langsung
        setUsers(response.data.map(user => ({
          ...user,
          bookings: user.bookings || 0,
          createdAt: user.created_at ? new Date(user.created_at).toLocaleDateString('id-ID') : '-'
        })));
        setTotalPages(Math.max(1, Math.ceil(response.data.length / 10)));
      } else {
        console.error('Unexpected response format:', response.data);
        setError('Format data tidak sesuai');
        setUsers([]);
        setTotalPages(1);
      }
    } catch (error) {
      setError('Gagal memuat data pengguna: ' + (error.response?.data?.message || error.message));
      setUsers([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterRole, searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleRoleChange = (e) => {
    setFilterRole(e.target.value);
    setCurrentPage(1);
  };

  // Form handling functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserForm({
      ...userForm,
      [name]: value
    });

    // Clear validation error when user types
    if (['name', 'email', 'password'].includes(name) && validation[name] === false) {
      setValidation({
        ...validation,
        [name]: true
      });
    }
  };

  const validateForm = () => {
    const newValidation = {
      name: userForm.name.trim() !== '',
      email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email),
      password: modalType === 'add' ? userForm.password.length >= 6 : true
    };

    setValidation(newValidation);
    return Object.values(newValidation).every(valid => valid);
  };

  const resetForm = () => {
    setUserForm({
      name: '',
      email: '',
      password: '',
      role: 'customer',
      status: 'active',
      phone: '',
      address: '',
      experience: '',
    });
    setValidation({
      name: true,
      email: true,
      password: true
    });
    setFormError('');
  };

  const prepareFormForEdit = (user) => {
    setUserForm({
      name: user.name || '',
      email: user.email || '',
      password: '', // Don't populate password for security
      role: user.role || 'customer',
      status: user.status || 'active',
      phone: user.phone || '',
      address: user.address || '',
      experience: user.experience || '',
    });
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setFormSubmitting(true);
    setFormError('');

    try {
      // Prepare the request data
      const userData = {
        name: userForm.name,
        email: userForm.email,
        role: userForm.role,
        status: userForm.status,
        phone: userForm.phone,
        address: userForm.address
      };

      // Add specific fields based on role
      if (userForm.role === 'mechanic') {
        userData.experience = parseInt(userForm.experience);
      }

      // Add password only if provided (required for new users)
      if (userForm.password) {
        userData.password = userForm.password;
      }

      if (modalType === 'add') {
        // Create new user
        await API.post('/api/user', userData);
      } else {
        // Update existing user
        await API.put(`/api/user/${selectedUser.id}`, userData);
      }

      // Refresh the user list
      fetchUsers();

      // Close the modal and reset form
      closeModal();

      // Show success message
      alert(
        modalType === 'add'
          ? 'Pengguna berhasil ditambahkan!'
          : 'Pengguna berhasil diperbarui!'
      );
    } catch (error) {
      console.error('Error submitting user:', error);
      setFormError(
        error.response?.data?.message ||
        'Terjadi kesalahan saat menyimpan data pengguna'
      );
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleAddUser = () => {
    resetForm(); // Reset form before opening
    setModalType('add');
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user) => {
    prepareFormForEdit(user);
    setModalType('edit');
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleDeleteUser = (user) => {
    setModalType('delete');
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const confirmDelete = async () => {
    try {
      // Gunakan API yang benar untuk menghapus user
      await API.delete(`/api/user/${selectedUser.id}`);
      alert('Pengguna berhasil dihapus');
      fetchUsers();
      closeModal();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Gagal menghapus pengguna: ' + (error.response?.data?.message || error.message));
      closeModal();
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return (
          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-semibold">
            Admin
          </span>
        );
      case 'staff':
        return (
          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
            Staff
          </span>
        );
      case 'mechanic':
        return (
          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
            Montir
          </span>
        );
      case 'customer':
        return (
          <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-semibold">
            Pelanggan
          </span>
        );
      case 'owner':
        return (
          <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold">
            Owner
          </span>
        );
      default:
        return null;
    }
  };

  const renderPagination = () => {
    return (
      <div className="flex justify-center mt-6">
        <nav className="flex items-center">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Sebelumnya
          </button>

          {/* Page Numbers */}
          <div className="hidden md:flex">
            {[...Array(totalPages).keys()].map((_, index) => {
              const pageNumber = index + 1;
              // Show only current page, first and last page, and up to 1 page on either side of current
              const shouldShow =
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);

              if (!shouldShow) {
                // Show ellipsis instead of page numbers
                if (pageNumber === 2 || pageNumber === totalPages - 1) {
                  return (
                    <div
                      key={`ellipsis-${pageNumber}`}
                      className="px-3 py-1 border-t border-b border-gray-300 bg-white text-sm text-gray-700"
                    >
                      ...
                    </div>
                  );
                }
                return null;
              }

              return (
                <button
                  key={pageNumber}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={`px-3 py-1 border-t border-b border-gray-300 text-sm font-medium ${currentPage === pageNumber
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          {/* Mobile Pagination */}
          <div className="px-4 py-1 border-t border-b border-gray-300 bg-white text-sm text-gray-700 md:hidden">
            Halaman {currentPage} dari {totalPages}
          </div>

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Berikutnya
          </button>
        </nav>
      </div>
    );
  };

  const renderDeleteModal = () => {
    if (!selectedUser) return null;

    return (
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold text-secondary mb-4">Hapus Pengguna</h2>
        <p className="mb-6">
          Apakah Anda yakin ingin menghapus pengguna <strong>{selectedUser.name}</strong>?
        </p>
        <div className="flex justify-end">
          <button
            onClick={closeModal}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 mr-2"
          >
            Batal
          </button>
          <button
            onClick={confirmDelete}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Hapus
          </button>
        </div>
      </div>
    );
  };

  const renderUserForm = () => {
    const isAddMode = modalType === 'add';
    const title = isAddMode ? 'Tambah Pengguna Baru' : 'Edit Pengguna';
    const submitText = isAddMode ? 'Tambah' : 'Simpan Perubahan';

    return (
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-secondary">{title}</h2>
          <button
            onClick={closeModal}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <FiX size={24} />
          </button>
        </div>

        {formError && (
          <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <div className="flex items-center">
              <FiAlertCircle className="mr-2" />
              <span>{formError}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmitUser}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700 border-b pb-2">Informasi Dasar</h3>

              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nama Lengkap <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={userForm.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${validation.name ? 'border-gray-300' : 'border-red-500'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  required
                />
                {!validation.name && (
                  <p className="mt-1 text-sm text-red-500">Nama tidak boleh kosong</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={userForm.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${validation.email ? 'border-gray-300' : 'border-red-500'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  required
                />
                {!validation.email && (
                  <p className="mt-1 text-sm text-red-500">Format email tidak valid</p>
                )}
              </div>

              {/* Password - Required for new users, optional for edit */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password {isAddMode && <span className="text-red-500">*</span>}
                  {!isAddMode && <span className="text-xs text-gray-500">(Biarkan kosong jika tidak ingin mengubah)</span>}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={userForm.password}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${validation.password ? 'border-gray-300' : 'border-red-500'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  required={isAddMode}
                />
                {!validation.password && (
                  <p className="mt-1 text-sm text-red-500">Password minimal 6 karakter</p>
                )}
              </div>

              {/* Role */}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  id="role"
                  name="role"
                  value={userForm.role}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                >
                  <option value="customer">Pelanggan</option>
                  <option value="mechanic">Montir</option>
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={userForm.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="active">Aktif</option>
                  <option value="inactive">Tidak Aktif</option>
                </select>
              </div>
            </div>

            {/* Role-specific fields */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700 border-b pb-2">
                {userForm.role === 'customer'
                  ? 'Informasi Pelanggan'
                  : userForm.role === 'mechanic'
                    ? 'Informasi Montir'
                    : 'Informasi Tambahan'}
              </h3>

              {/* Phone - for customers and mechanics */}
              {(userForm.role === 'customer' || userForm.role === 'mechanic') && (
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Nomor Telepon
                  </label>
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    value={userForm.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              )}

              {/* Address - for customers */}
              {userForm.role === 'customer' && (
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                    Alamat
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    value={userForm.address}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              )}

              {/* Mechanic experience */}
              {userForm.role === 'mechanic' && (
                <div>
                  <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
                    Pengalaman (Tahun) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="experience"
                    name="experience"
                    value={userForm.experience}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    required={userForm.role === 'mechanic'}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 mr-2"
              disabled={formSubmitting}
            >
              Batal
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark flex items-center"
              disabled={formSubmitting}
            >
              {formSubmitting ? (
                <>
                  <div className="animate-spin h-4 w-4 mr-2 border-t-2 border-b-2 border-white rounded-full"></div>
                  <span>Menyimpan...</span>
                </>
              ) : (
                <>
                  <FiCheck className="mr-2" />
                  <span>{submitText}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <DashboardLayout userRole="admin" userName="Admin">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Manajemen Pengguna</h1>
          <p className="text-gray-600">Kelola semua pengguna sistem</p>
        </div>
        <button
          onClick={handleAddUser}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center"
        >
          <FiUserPlus className="mr-2" /> Tambah Pengguna
        </button>
      </div>

      {/* Search and Filter */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <form onSubmit={handleSearch} className="flex mb-4 md:mb-0">
            <div className="relative">
              <input
                type="text"
                placeholder="Cari nama atau email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-l focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <button
              type="submit"
              className="bg-primary text-white px-4 py-2 rounded-r hover:bg-primary-dark"
            >
              Cari
            </button>
          </form>

          <div className="flex items-center">
            <span className="mr-2 text-gray-500">Filter:</span>
            <select
              value={filterRole}
              onChange={handleRoleChange}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">Semua Role</option>
              <option value="admin">Admin</option>
              <option value="staff">Staff</option>
              <option value="mechanic">Montir</option>
              <option value="customer">Pelanggan</option>
              <option value="owner">Owner</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 relative">
          <strong className="font-bold mr-1">Error:</strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-16">
            <FiUsers size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">Tidak ada pengguna yang ditemukan</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nama</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kontak</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Terdaftar</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt={user.name} className="h-10 w-10 rounded-full" />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                              {user.initials || user.name?.substring(0, 2) || 'U'}
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.email}</div>
                      <div className="text-sm text-gray-500">{user.phone || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.createdAt}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        {user.status === 'active' ? (
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
                            Aktif
                          </span>
                        ) : user.status === 'inactive' ? (
                          <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold">
                            Tidak Aktif
                          </span>
                        ) : (
                          <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-semibold">
                            {user.status || 'Tidak Diketahui'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-yellow-600 hover:text-yellow-900 mr-3"
                        title="Edit Pengguna"
                      >
                        <FiEdit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="text-red-600 hover:text-red-900"
                        title="Hapus Pengguna"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && users.length > 0 && renderPagination()}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          {modalType === 'delete' && renderDeleteModal()}
          {(modalType === 'add' || modalType === 'edit') && renderUserForm()}
        </div>
      )}
    </DashboardLayout>
  );
};

export default AdminUsers; 
